import React, { useState, useEffect, useRef } from 'react';
import { sendMessageToGemini, generateTimetableSuggestion } from '../../services/gemini';
import type { ChatMessage } from '../../services/gemini';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface AIAssistantProps {
  domain: string;
  onBack: () => void;
}

const AIAssistant: React.FC<AIAssistantProps> = ({ domain, onBack }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'chat' | 'timetable' | 'schedule'>('chat');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Timetable generation states
  const [classData, setClassData] = useState({
    className: '',
    section: '',
    subjects: [] as string[],
    teachers: [] as { name: string; subject: string }[],
    periodsPerDay: 8,
    workingDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
  });

  const [availableClasses, setAvailableClasses] = useState<string[]>([]);
  const [availableTeachers, setAvailableTeachers] = useState<any[]>([]);

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load initial data
  useEffect(() => {
    loadClasses();
    loadTeachers();
    // Add welcome message
    setMessages([
      {
        role: 'model',
        parts: 'Hello! I\'m your AI assistant for Smart School Management. I can help you with:\n\n• Creating and optimizing timetables\n• Generating schedules\n• Analyzing data\n• Providing administrative insights\n\nHow can I assist you today?',
        timestamp: new Date().toISOString(),
      },
    ]);
  }, []);

  const loadClasses = async () => {
    try {
      const classesRef = collection(db, `school-data/${domain}/classes`);
      const snapshot = await getDocs(classesRef);
      const classes = snapshot.docs.map(doc => doc.id);
      setAvailableClasses(classes);
    } catch (error) {
      console.error('Error loading classes:', error);
    }
  };

  const loadTeachers = async () => {
    try {
      const teachersRef = collection(db, `school-data/${domain}/teachers`);
      const snapshot = await getDocs(teachersRef);
      const teachers = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAvailableTeachers(teachers);
    } catch (error) {
      console.error('Error loading teachers:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage: ChatMessage = {
      role: 'user',
      parts: inputMessage,
      timestamp: new Date().toISOString(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await sendMessageToGemini(inputMessage, messages);
      const aiMessage: ChatMessage = {
        role: 'model',
        parts: response,
        timestamp: new Date().toISOString(),
      };
      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      const errorMessage: ChatMessage = {
        role: 'model',
        parts: 'Sorry, I encountered an error. Please try again or check your API configuration.',
        timestamp: new Date().toISOString(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateTimetable = async () => {
    if (!classData.className || !classData.section) {
      alert('Please select class and section');
      return;
    }

    setIsLoading(true);
    setActiveTab('chat');

    try {
      const response = await generateTimetableSuggestion(classData);
      const aiMessage: ChatMessage = {
        role: 'model',
        parts: response,
        timestamp: new Date().toISOString(),
      };
      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      const errorMessage: ChatMessage = {
        role: 'model',
        parts: 'Failed to generate timetable. Please check your configuration.',
        timestamp: new Date().toISOString(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const addSubject = () => {
    const subject = prompt('Enter subject name:');
    if (subject) {
      setClassData(prev => ({
        ...prev,
        subjects: [...prev.subjects, subject],
      }));
    }
  };

  const removeSubject = (index: number) => {
    setClassData(prev => ({
      ...prev,
      subjects: prev.subjects.filter((_, i) => i !== index),
    }));
  };

  const addTeacher = () => {
    if (availableTeachers.length === 0) {
      alert('No teachers available. Please add teachers first.');
      return;
    }

    const teacherId = prompt(`Select teacher by ID:\n${availableTeachers.map(t => `${t.teacherId}: ${t.name}`).join('\n')}`);
    const teacher = availableTeachers.find(t => t.teacherId === teacherId);

    if (teacher) {
      const subject = prompt('Enter subject for this teacher:');
      if (subject) {
        setClassData(prev => ({
          ...prev,
          teachers: [...prev.teachers, { name: teacher.name, subject }],
        }));
      }
    }
  };

  const removeTeacher = (index: number) => {
    setClassData(prev => ({
      ...prev,
      teachers: prev.teachers.filter((_, i) => i !== index),
    }));
  };

  const quickPrompts = [
    'How do I create an optimal timetable?',
    'What are best practices for scheduling exams?',
    'Help me allocate teachers efficiently',
    'Suggest a balanced weekly schedule',
  ];

  return (
    <div className="min-vh-100 bg-light">
      {/* Header */}
      <div className="bg-primary-custom text-white p-3 shadow-sm">
        <div className="container-fluid d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-3">
            <i className="bi bi-robot" style={{ fontSize: '2rem' }}></i>
            <div>
              <h4 className="mb-0">AI Assistant</h4>
              <small className="opacity-75">Powered by Google Gemini</small>
            </div>
          </div>
          <button className="btn btn-light btn-sm" onClick={onBack}>
            <i className="bi bi-arrow-left me-2"></i>Back to Dashboard
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white shadow-sm">
        <div className="container-fluid">
          <ul className="nav nav-tabs border-0">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'chat' ? 'active' : ''}`}
                onClick={() => setActiveTab('chat')}
              >
                <i className="bi bi-chat-dots me-2"></i>Chat
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'timetable' ? 'active' : ''}`}
                onClick={() => setActiveTab('timetable')}
              >
                <i className="bi bi-calendar-week me-2"></i>Timetable Generator
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'schedule' ? 'active' : ''}`}
                onClick={() => setActiveTab('schedule')}
              >
                <i className="bi bi-calendar-event me-2"></i>Schedule Helper
              </button>
            </li>
          </ul>
        </div>
      </div>

      {/* Content Area */}
      <div className="container-fluid py-4">
        {activeTab === 'chat' && (
          <div className="row justify-content-center">
            <div className="col-lg-10 col-xl-8">
              <div className="card shadow-sm">
                <div className="card-body p-0" style={{ height: '600px', display: 'flex', flexDirection: 'column' }}>
                  {/* Messages Area */}
                  <div className="flex-grow-1 p-4 overflow-auto" style={{ maxHeight: 'calc(100% - 150px)' }}>
                    {messages.map((msg, index) => (
                      <div
                        key={index}
                        className={`d-flex mb-3 ${msg.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}
                      >
                        <div
                          className={`p-3 rounded-3 ${
                            msg.role === 'user'
                              ? 'bg-primary-custom text-white'
                              : 'bg-light border'
                          }`}
                          style={{ maxWidth: '75%' }}
                        >
                          {msg.role === 'model' && (
                            <div className="d-flex align-items-center gap-2 mb-2">
                              <i className="bi bi-robot text-primary-custom"></i>
                              <strong className="text-primary-custom">AI Assistant</strong>
                            </div>
                          )}
                          <div style={{ whiteSpace: 'pre-wrap' }}>{msg.parts}</div>
                          <small className={`d-block mt-2 opacity-75 ${msg.role === 'user' ? 'text-end' : ''}`}>
                            {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString() : ''}
                          </small>
                        </div>
                      </div>
                    ))}
                    {isLoading && (
                      <div className="d-flex justify-content-start mb-3">
                        <div className="bg-light border p-3 rounded-3">
                          <div className="spinner-border spinner-border-sm text-primary-custom me-2" role="status"></div>
                          AI is thinking...
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Quick Prompts */}
                  <div className="px-4 py-2 bg-light border-top border-bottom">
                    <small className="text-muted d-block mb-2">Quick prompts:</small>
                    <div className="d-flex gap-2 flex-wrap">
                      {quickPrompts.map((prompt, index) => (
                        <button
                          key={index}
                          className="btn btn-sm btn-outline-primary rounded-pill"
                          onClick={() => setInputMessage(prompt)}
                          disabled={isLoading}
                        >
                          {prompt}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Input Area */}
                  <div className="p-3 bg-white border-top">
                    <div className="input-group">
                      <textarea
                        className="form-control"
                        placeholder="Type your message here..."
                        value={inputMessage}
                        onChange={(e) => setInputMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        rows={2}
                        disabled={isLoading}
                      />
                      <button
                        className="btn btn-primary-custom"
                        onClick={handleSendMessage}
                        disabled={isLoading || !inputMessage.trim()}
                      >
                        <i className="bi bi-send-fill"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'timetable' && (
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="card shadow-sm">
                <div className="card-header bg-primary-custom text-white">
                  <h5 className="mb-0">
                    <i className="bi bi-calendar-week me-2"></i>AI Timetable Generator
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Class</label>
                      <select
                        className="form-select"
                        value={classData.className}
                        onChange={(e) => setClassData({ ...classData, className: e.target.value })}
                      >
                        <option value="">Select Class</option>
                        {availableClasses.map(cls => (
                          <option key={cls} value={cls}>{cls}</option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-6">
                      <label className="form-label">Section</label>
                      <select
                        className="form-select"
                        value={classData.section}
                        onChange={(e) => setClassData({ ...classData, section: e.target.value })}
                      >
                        <option value="">Select Section</option>
                        <option value="A">A</option>
                        <option value="B">B</option>
                        <option value="C">C</option>
                      </select>
                    </div>

                    <div className="col-md-6">
                      <label className="form-label">Periods Per Day</label>
                      <input
                        type="number"
                        className="form-control"
                        value={classData.periodsPerDay}
                        onChange={(e) => setClassData({ ...classData, periodsPerDay: parseInt(e.target.value) })}
                        min="4"
                        max="10"
                      />
                    </div>

                    <div className="col-12">
                      <label className="form-label">Subjects</label>
                      <div className="d-flex gap-2 flex-wrap mb-2">
                        {classData.subjects.map((subject, index) => (
                          <span key={index} className="badge bg-primary-custom p-2">
                            {subject}
                            <button
                              className="btn-close btn-close-white ms-2"
                              style={{ fontSize: '0.6rem' }}
                              onClick={() => removeSubject(index)}
                            ></button>
                          </span>
                        ))}
                      </div>
                      <button className="btn btn-sm btn-outline-primary" onClick={addSubject}>
                        <i className="bi bi-plus-circle me-2"></i>Add Subject
                      </button>
                    </div>

                    <div className="col-12">
                      <label className="form-label">Teachers</label>
                      <div className="d-flex gap-2 flex-wrap mb-2">
                        {classData.teachers.map((teacher, index) => (
                          <span key={index} className="badge badge-secondary-custom p-2">
                            {teacher.name} ({teacher.subject})
                            <button
                              className="btn-close ms-2"
                              style={{ fontSize: '0.6rem' }}
                              onClick={() => removeTeacher(index)}
                            ></button>
                          </span>
                        ))}
                      </div>
                      <button className="btn btn-sm btn-outline-primary" onClick={addTeacher}>
                        <i className="bi bi-plus-circle me-2"></i>Add Teacher
                      </button>
                    </div>

                    <div className="col-12">
                      <button
                        className="btn btn-primary-custom w-100"
                        onClick={handleGenerateTimetable}
                        disabled={isLoading || !classData.className || !classData.section}
                      >
                        {isLoading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Generating Timetable...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-magic me-2"></i>Generate Timetable with AI
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'schedule' && (
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="card shadow-sm">
                <div className="card-header bg-primary-custom text-white">
                  <h5 className="mb-0">
                    <i className="bi bi-calendar-event me-2"></i>Schedule Helper
                  </h5>
                </div>
                <div className="card-body">
                  <div className="alert alert-info">
                    <i className="bi bi-info-circle me-2"></i>
                    Use the chat to ask questions about scheduling, exam planning, event management, and more!
                  </div>

                  <div className="row g-3">
                    <div className="col-md-4">
                      <div className="card h-100 border-primary">
                        <div className="card-body text-center">
                          <i className="bi bi-calendar-check text-primary-custom" style={{ fontSize: '2rem' }}></i>
                          <h6 className="mt-3">Exam Scheduling</h6>
                          <p className="small text-muted">Get AI suggestions for optimal exam schedules</p>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => {
                              setActiveTab('chat');
                              setInputMessage('Help me create an exam schedule for end-term exams');
                            }}
                          >
                            Start Planning
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="card h-100 border-primary">
                        <div className="card-body text-center">
                          <i className="bi bi-clipboard-check text-primary-custom" style={{ fontSize: '2rem' }}></i>
                          <h6 className="mt-3">Event Planning</h6>
                          <p className="small text-muted">Plan school events and activities</p>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => {
                              setActiveTab('chat');
                              setInputMessage('Help me plan a school annual day event schedule');
                            }}
                          >
                            Start Planning
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="card h-100 border-primary">
                        <div className="card-body text-center">
                          <i className="bi bi-person-workspace text-primary-custom" style={{ fontSize: '2rem' }}></i>
                          <h6 className="mt-3">Resource Optimization</h6>
                          <p className="small text-muted">Optimize teacher and classroom allocation</p>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => {
                              setActiveTab('chat');
                              setInputMessage('How can I optimize teacher allocation across classes?');
                            }}
                          >
                            Get Insights
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Info Footer */}
      <div className="container-fluid mt-4">
        <div className="card">
          <div className="card-footer bg-light">
            <small className="text-muted d-block">
              <i className="bi bi-shield-check me-2"></i>
              <strong>Note:</strong> This AI assistant uses Google Gemini to provide intelligent suggestions.
              Always review AI-generated schedules and make adjustments based on your specific needs.
            </small>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAssistant;
